import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRulerHorizontal, faXmark } from "@fortawesome/pro-regular-svg-icons";
import * as OBF from "@thatopen/components-front";
import {faRotateLeft, faTriangleExclamation} from "@fortawesome/pro-solid-svg-icons";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import useProjectFilePlanContext from "./context/use-project-file-plan-context.ts";

import Card from "@/components/card";
import DropdownSelector from "@/components/dropdown-selector";


import ProjectEditButton from "@/components/buttons/project-edit-button";
import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";

import Button from "@/components/buttons/button";
import CheckBox from "@/components/checkbox";

import SimpleLoadingIndicator from "@/components/simple-loading-indicator/index.tsx";
import euclideanDistance from "../../helpers/euclidean-distance.ts";

import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import ScaleModeType, {
    FileContentTypeDescription,
    labels
} from "@/pages/takeoff/context/project-organisation/models/enums/file-content-type.ts";
import PlanFormat from "@/pages/takeoff/context/project-organisation/types/plan-format.ts";
import PlanScale, { PlanScales } from "@/pages/takeoff/context/project-organisation/types/plan-scale.ts";
import PlanOrientation, { PlanOrientations } from "@/pages/takeoff/context/project-organisation/types/plan-orientation.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";

import { OrganisationPlanViewerContainerRef } from "./organisation-plan-viewer-container.tsx";

export interface PlanDataDetailsRef {
    setLine: (line: OBF.Line) => void;
    setIsScaleInvalid: (isInvalid: boolean) => void;
}

interface PlanDataDetailsProps {
    viewerRef: React.RefObject<OrganisationPlanViewerContainerRef>;
}

const PlanDataDetails = forwardRef<PlanDataDetailsRef, PlanDataDetailsProps>(({ viewerRef }, ref) => {
    const { canEdit, ensureCanEdit } = useProjectEditAccess();
    const { dataContent, dispatch } = useProjectFilePlanContext();
    const { setIsInDrawingMode } = useModelingToolsContext();

    const [isDefiningMeasure, setIsDefiningMeasure] = useState(false);
    const [isConfirmingMeasure, setIsConfirmingMeasure] = useState(false);

    const [tempLine, setTempLine] = useState<OBF.Line | undefined>(undefined);

    const [isScaleInvalid, setIsScaleInvalid] = useState(false);

    const [applyNewScale, setApplyNewScale] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
        setLine: (line: OBF.Line) => {
            // console.log("Line received in PlanDataDetails:", line);
            setTempLine(line);
        },
        setIsScaleInvalid: (isInvalid: boolean) => setIsScaleInvalid(isInvalid)
    }));

    const handleDefineNewScale = useCallback(() => {
        setIsDefiningMeasure(true);
        setIsInDrawingMode(true);
    }, [setIsInDrawingMode]);

    const closeDefineNewScale = useCallback(() => {
        setIsDefiningMeasure(false);
        setTempLine(undefined);
        viewerRef.current?.clearDrawingTool();
    }, [viewerRef]);

    const handleConfirmNewScale = useCallback(() => {
        setIsConfirmingMeasure(true);
        setIsInDrawingMode(true)
    }, [setIsInDrawingMode]);

    const closeConfirmingScale = useCallback(() => {
        setIsConfirmingMeasure(false);
        setApplyNewScale(false);
        setTempLine(undefined);
        viewerRef.current?.clearDrawingTool();
    }, [viewerRef]);

    const reset = useCallback(() => {
        setIsDefiningMeasure(false);
        setApplyNewScale(false);
        setIsConfirmingMeasure(false);
        setTempLine(undefined);
    }, []);

    const handleValidateNewScale = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!ensureCanEdit()) {
            return;
        }

        if (dataContent === undefined || dataContent instanceof KnownScaleContent) return;
        if (tempLine === undefined || tempLine.value === undefined) return;
        const formData = new FormData(event.currentTarget);
        const newScaleValue = Number(formData.get("newScaleValue"));
        if (!newScaleValue || newScaleValue <= 0) return;

        const newScale = Math.round(newScaleValue / tempLine.value * 1000) / 1000;
        dispatch({ type: 'change-scale-from-measure', newScale: newScale });
        dispatch({ type: 'change-segment', segment: { start: { x: tempLine.start.x * newScale, y: -tempLine.start.z * newScale }, end: { x: tempLine.end.x * newScale, y: -tempLine.end.z * newScale } } });
        viewerRef.current?.updateLoaderOptions("UserDefinedContent", undefined, undefined, undefined, newScale);
        viewerRef.current?.applyNewScale(newScale);
        reset();
    };

    const handleConfirmScale = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!ensureCanEdit()) {
            return;
        }

        if (dataContent === undefined) return;
        if (tempLine?.value === undefined || tempLine === undefined) return;
        const formData = new FormData(event.currentTarget);
        const newScaleValue = Number(formData.get("newScaleValue"));
        if (!newScaleValue || newScaleValue <= 0) return;

        if (dataContent instanceof KnownScaleContent) {
            dispatch({ type: 'change-content-type', contentType: ScaleModeType.USER });
        }

        const newScale = Math.round(newScaleValue / tempLine.value * 1000) / 1000;
        dispatch({ type: 'change-scale-from-measure', newScale: newScale });
        dispatch({ type: 'change-segment', segment: { start: { x: tempLine.start.x * newScale, y: -tempLine.start.z * newScale }, end: { x: tempLine.end.x * newScale, y: -tempLine.end.z * newScale } } });
        viewerRef.current?.updateLoaderOptions("UserDefinedContent", undefined, undefined, undefined, newScale);
        viewerRef.current?.applyNewScale(newScale);
        reset();
    };

    const contentType = useMemo(() => {
        if (dataContent === undefined) return undefined;
        if (dataContent instanceof KnownScaleContent) return ScaleModeType.KNOWN;
        return ScaleModeType.USER;
    }, [dataContent]);

    return (
        dataContent === undefined ? (
            <Card additionalClasses='h-fit w-fit px-4 py-2'>
                <SimpleLoadingIndicator />
            </Card>
        ) : (
            <>
                <Card additionalClasses='select-none h-fit'>
                    <div className='flex flex-col gap-y-2 px-4 py-3'>
                        <div className='grid grid-cols-[auto_1fr] gap-y-3 gap-x-4 items-center'>
                            <div className='flex flex-row gap-x-2 items-center col-span-2'>
                                <DropdownSelector disabled={!canEdit} items={labels}
                                    keySelector={l => l.contentType}
                                    selectedItem={labels.find(l => l.contentType === contentType)}
                                    displaySelector={l => l.description}
                                    className='flex-1'
                                    onSelectionChanged={function (selectedItem: FileContentTypeDescription | null): void {
                                        if (selectedItem)
                                            dispatch({
                                                type: 'change-content-type',
                                                contentType: selectedItem.contentType
                                            });
                                    }} />

                                {isScaleInvalid && (
                                    <span title="Attention : proportion échelle non valide">
                                        <FontAwesomeIcon icon={faTriangleExclamation} className='text-yellow-500 text-3xl' />
                                    </span>
                                )}
                            </div>

                            {dataContent instanceof KnownScaleContent && (
                                <>
                                    <div className='my-auto'>Format</div>
                                    <DropdownSelector disabled={!canEdit} items={['A0', 'A1', 'A2', 'A3', 'A4']}
                                        selectedItem={dataContent.format}
                                        onSelectionChanged={function (selectedItem: PlanFormat | null): void {
                                            if (selectedItem) {
                                                dispatch({
                                                    type: 'change-format',
                                                    format: selectedItem
                                                });
                                                viewerRef.current?.updateLoaderOptions("KnownScaleContent", selectedItem, dataContent.scale, dataContent.orientation, undefined);
                                            }
                                        }} />

                                    <div className='my-auto'>Echelle</div>
                                    <div className='flex flex-col gap-2'>
                                        <div className='flex items-center gap-x-4'>
                                            <div className='flex-1'>
                                                <DropdownSelector disabled={!canEdit} items={PlanScales}
                                                    selectedItem={dataContent.scale}
                                                    onSelectionChanged={function (selectedItem: PlanScale | null): void {
                                                        if (selectedItem) {
                                                            dispatch({
                                                                type: 'change-scale',
                                                                scale: selectedItem
                                                            });
                                                            viewerRef.current?.updateLoaderOptions("KnownScaleContent", dataContent.format, selectedItem, dataContent.orientation, undefined);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className='my-auto'>Orientation</div>
                                    <div className='flex flex-col gap-2'>
                                        <div className='flex items-center gap-x-4'>
                                            <div className='flex-1'>
                                                <DropdownSelector disabled={!canEdit} items={PlanOrientations}
                                                    selectedItem={dataContent.orientation}
                                                    onSelectionChanged={function (selectedItem: PlanOrientation | null): void {
                                                        if (selectedItem) {
                                                            dispatch({
                                                                type: 'change-orientation',
                                                                orientation: selectedItem
                                                            });
                                                            viewerRef.current?.updateLoaderOptions("KnownScaleContent", dataContent.format, dataContent.scale, selectedItem, undefined);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            {dataContent instanceof UserDefinedContent && (
                                <>
                                    <FontAwesomeIcon icon={faRulerHorizontal} className='my-auto text-2xl text-gray-800 ml-px' />

                                    <div className='flex flex-row flex-1 gap-x-4 items-center'>
                                        <span>{dataContent.segment ?
                                            Math.round(euclideanDistance(
                                                dataContent.segment.start.x,
                                                dataContent.segment.start.y,
                                                dataContent.segment.end.x,
                                                dataContent.segment.end.y
                                            ) * 100) / 100 : '--'} m</span>

                                        <ProjectEditButton className='ml-auto' onClick={handleDefineNewScale} disabled={isConfirmingMeasure || isDefiningMeasure}>
                                            <div className='w-full'>Définir</div>
                                        </ProjectEditButton>
                                    </div>
                                </>
                            )}
                        </div>

                        <ProjectEditButton variant='simple' size='xs' className='ml-auto mt-2' onClick={handleConfirmNewScale} disabled={isConfirmingMeasure || isDefiningMeasure}>
                            Vérifier l’échelle avec une cote
                        </ProjectEditButton>
                    </div>
                </Card>

                {isDefiningMeasure && (
                    <Card additionalClasses='h-fit'>
                        <form onSubmit={handleValidateNewScale} className='flex flex-col gap-x-4 text-sm'>
                            <div className='flex flex-row gap-x-4 px-4 py-2 border-b border-slate-300'>
                                <div className='text-sm'>Définir l'échelle</div>

                                <Button variant='ghost' size='xs' className='ml-auto' onClick={closeDefineNewScale}>
                                    <FontAwesomeIcon icon={faXmark} className='text-red-500' />
                                </Button>
                            </div>
                            <div className='grid grid-cols-[auto_1fr] gap-2 px-4 py-2 items-center'>
                                <div>Dessiner</div>
                                <Button disabled={!canEdit} variant='ghost' size='sm' onClick={() => setIsInDrawingMode(true)} className='mr-auto'>
                                    <FontAwesomeIcon icon={faRotateLeft} className='text-green-600' size='sm'/>
                                </Button>

                                {tempLine && (
                                    <>
                                        <div>Valeur mesurée :</div>
                                        <div>{tempLine.value} m</div>
                                        <div className='my-auto'>Nouvelle valeur :</div>

                                        <input disabled={!canEdit} type='number' name='newScaleValue' step='any' min='0.001' required
                                            className="no-spinner px-2.5 py-0 w-20 rounded-md bg-white text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600" />
                                        <div />

                                        <Button disabled={!canEdit} type='submit'>Appliquer</Button>
                                    </>
                                )}
                            </div>
                        </form>
                    </Card>
                )}

                {isConfirmingMeasure && (
                    <Card additionalClasses='h-fit'>
                        <form onSubmit={handleConfirmScale} className='flex flex-col gap-x-4 text-sm'>
                            <div className='flex flex-row gap-x-4 px-4 py-2 border-b border-slate-300'>
                                <div className='text-sm'>Vérifier l'échelle</div>

                                <Button variant='ghost' size='xs' className='ml-auto' onClick={closeConfirmingScale}>
                                    <FontAwesomeIcon icon={faXmark} className='text-red-500' />
                                </Button>
                            </div>
                            <div className='grid grid-cols-[auto_1fr] gap-2 px-4 py-2'>
                                <div className='my-auto'>Dessiner</div>
                                <Button disabled={!canEdit} variant='ghost' size='sm' onClick={() => setIsInDrawingMode(true)} className='mr-auto'>
                                    <FontAwesomeIcon icon={faRotateLeft} className='text-green-600' size='sm'/>
                                </Button>

                                {tempLine && (
                                    <>
                                        <div>Valeur mesurée :</div>
                                        <div>{tempLine.value} m</div>

                                        {dataContent instanceof KnownScaleContent ? (
                                            <>
                                                <div/>
                                                <Button onClick={() => {
                                                    setApplyNewScale(true);
                                                    dispatch({
                                                        type: 'change-content-type',
                                                        contentType: ScaleModeType.USER
                                                    });
                                                }}>Changer de mode pour réctifier la valeur</Button>
                                            </>
                                        ) : (
                                            <>
                                                {!applyNewScale ? (
                                                    <>
                                                        <span>Appliquer une nouvelle valeur</span>
                                                        <CheckBox checked={applyNewScale}
                                                                  onChange={(e) => setApplyNewScale(e.target.checked)}
                                                                  className='my-auto'/>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className='my-auto'>Nouvelle valeur :</div>

                                                        <input disabled={!canEdit} type='number' name='newScaleValue' step='any' min='0.001'
                                                               required
                                                               className="no-spinner px-2.5 py-0 w-20 rounded-md bg-white text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"/>
                                                        <div/>

                                                        <Button disabled={!canEdit} type='submit' className='ml-auto'>Appliquer</Button>
                                                    </>
                                                )}</>
                                        )}
                                    </>
                                )}
                            </div>
                        </form>
                    </Card>
                )}
            </>
        )
    )
});

export default PlanDataDetails;